import { compareAmounts, parseAmount } from "@/helpers/money"
import { IInventoryItem } from "@/store/apis/inventoryAPI/inventory.types"
import { ISortContext, ISortOption } from "./types"

export const SORT_DEFAULT_KEY = "rarity_desc"

const UNKNOWN_RARITY_RANK = -Infinity

const UNKNOWN_CURRENCY_RANK = Infinity

// Comparison-based on purpose: subtraction with the -Infinity sentinel yields
// NaN for two unknown tiers and would silently skip the tie-break chain.
const compareValues = (a: number | string, b: number | string): number =>
  a < b ? -1 : a > b ? 1 : 0

// Shared fall-through chain: localised name (collator) → id (plain compare).
// The default sort therefore reads rarity → name → id.
const tieBreak = (
  a: IInventoryItem,
  b: IInventoryItem,
  ctx: ISortContext
): number => {
  const byName = ctx.collator.compare(
    ctx.nameByCode.get(a.definitionCode) ?? "",
    ctx.nameByCode.get(b.definitionCode) ?? ""
  )
  return byName !== 0 ? byName : compareValues(a.id, b.id)
}

const rarityRankOf = (item: IInventoryItem, ctx: ISortContext): number =>
  ctx.rarityRank.get(item.rarityCode) ?? UNKNOWN_RARITY_RANK

const VALUE_BY_ITEM = new WeakMap<IInventoryItem, bigint | null>()

const valueOf = (item: IInventoryItem): bigint | null => {
  const cached = VALUE_BY_ITEM.get(item)
  if (cached !== undefined) {
    return cached
  }
  const amount = parseAmount(item.price.amount)
  const value = amount !== null && amount > 0n ? amount : null
  VALUE_BY_ITEM.set(item, value)
  return value
}

const currencyRankOf = (item: IInventoryItem, ctx: ISortContext): number =>
  ctx.currencyRank.get(item.price.currencyCode) ?? UNKNOWN_CURRENCY_RANK

const comparePrices = (
  a: IInventoryItem,
  b: IInventoryItem,
  ctx: ISortContext
): number => {
  const aValue = valueOf(a)
  const bValue = valueOf(b)
  if (aValue === null || bValue === null) {
    return aValue === bValue ? 0 : aValue === null ? 1 : -1
  }
  const byCurrency = compareValues(
    currencyRankOf(a, ctx),
    currencyRankOf(b, ctx)
  )
  return byCurrency !== 0 ? byCurrency : compareAmounts(bValue, aValue)
}

export const SORT_OPTIONS: ISortOption[] = [
  {
    key: SORT_DEFAULT_KEY,
    labelKey: "sort.rarityDesc",
    comparator: (a, b, ctx) => {
      const byRarity = compareValues(rarityRankOf(b, ctx), rarityRankOf(a, ctx))
      return byRarity !== 0 ? byRarity : tieBreak(a, b, ctx)
    }
  },
  {
    key: "name_asc",
    labelKey: "sort.nameAsc",
    comparator: tieBreak
  },
  {
    key: "stack_weight_desc",
    labelKey: "sort.stackWeightDesc",
    comparator: (a, b, ctx) => {
      const byWeight = compareValues(b.stackWeight, a.stackWeight)
      return byWeight !== 0 ? byWeight : tieBreak(a, b, ctx)
    }
  },
  {
    key: "price_desc",
    labelKey: "sort.priceDesc",
    comparator: (a, b, ctx) => {
      const byPrice = comparePrices(a, b, ctx)
      return byPrice !== 0 ? byPrice : tieBreak(a, b, ctx)
    }
  }
]

export const getSortOption = (key: string): ISortOption =>
  SORT_OPTIONS.find((option) => option.key === key) ?? SORT_OPTIONS[0]
